import { Button } from '@ipfabric/ui-components/Button/Button';
import { TextField } from '@ipfabric/ui-components/TextField/TextField';
import { type ReactNode, useEffect } from 'react';
import {
  type Control,
  type FieldPath,
  type FieldValues,
  useController,
  useWatch,
} from 'react-hook-form';
import { useRegExpHasMatchingGroup } from './useRegExpHasMatchingGroup';

type RegexSiteNameFieldValues = FieldValues & {
  regex: string;
  siteName: string | null;
};

type RegexSiteNameFieldProps<
  TFieldValues extends RegexSiteNameFieldValues = RegexSiteNameFieldValues,
> = {
  control: Control<TFieldValues>;
  dataTestId: string;
  generatedSiteNameMessage?: string;
  label: ReactNode;
};

export const RegexSiteNameField = <
  TFieldValues extends RegexSiteNameFieldValues = RegexSiteNameFieldValues,
>({
  control,
  dataTestId,
  generatedSiteNameMessage = 'Generated from first capturing group',
  label,
}: RegexSiteNameFieldProps<TFieldValues>) => {
  const regex =
    useWatch({
      control,
      name: 'regex' as FieldPath<TFieldValues>,
    }) ?? '';
  const {
    field: { name, onBlur, onChange, ref, value },
    fieldState: { error },
  } = useController({
    control,
    name: 'siteName' as FieldPath<TFieldValues>,
  });

  const siteName = (value as string | null | undefined) ?? null;
  const hasMatchingGroup = useRegExpHasMatchingGroup(regex);
  const customNameUsed = hasMatchingGroup && siteName !== null;
  const siteNameDisabled = hasMatchingGroup && siteName === null;

  useEffect(() => {
    if (!hasMatchingGroup && siteName === null) {
      onChange('');
    }
  }, [hasMatchingGroup, onChange, siteName]);

  return (
    <div className='flex flex-row gap-2 items-start'>
      <div className='flex-1'>
        <TextField
          dataTestId={dataTestId}
          disabled={siteNameDisabled}
          error={typeof error?.message === 'string' ? error.message : undefined}
          id={name}
          inputRef={ref}
          label={label}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          type='text'
          // When disabled, show generated message while keeping form value as null.
          value={siteNameDisabled ? generatedSiteNameMessage : (siteName ?? '')}
        />
      </div>
      {hasMatchingGroup && (
        <Button
          className='mt-6 shrink-0'
          onClick={() => {
            onChange(customNameUsed ? null : '');
          }}
          size='default'
          type='button'
          variant='outline'
        >
          {customNameUsed ? 'Use default' : 'Customize'}
        </Button>
      )}
    </div>
  );
};
